import React from 'react';

const DashboardCards = ({
  userData,
  workerFinance,
  poolData,
  k1PoolData,
  hashrates,
  workers,
  balances,
  earnings,
  activeWorkers,
  totalColor,
  isMobile,
  kasPrice,
  priceLoading,
  priceError,
  miningTotal,
  miningBalanceLoading,
  miningBalanceError,
  ledgerTotal,
  styles,
  formatNumber,
  getExpectedHashrate,
  timeUntilRefresh,
  formatTime,
  isFinanceTab,
  kasChange24h,
  kaspaData,
  kaspaError,
  kaspaFetchTime,
  usdcMiningBalance,
}) => {
  // --- Electric cost projection ---
  let totalElectricPerDay = 0;
  let electricNextMonth = 0;

  if (Array.isArray(workerFinance)) {
    totalElectricPerDay = workerFinance
      .filter(wf => !wf.DateEnded && typeof wf.ElectricCostPerDay === 'number')
      .reduce((sum, wf) => sum + wf.ElectricCostPerDay, 0);

    const now = new Date();
    const year = now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear();
    const nextMonth = (now.getMonth() + 1) % 12;
    const daysInNextMonth = new Date(year, nextMonth + 1, 0).getDate();
    electricNextMonth = totalElectricPerDay * daysInNextMonth;
  }

  // --- KAS mining stats for projections ---
  let avgKasPerDay7 = 0;
  let avgUsdPerDay7 = 0;
  let sofarThisMonth = 0;
  let projectedThisMonth = 0;
  let totalKasThisMonth = 0;
  let projectedKASThisMonth = 0;

  if (Array.isArray(earnings) && earnings.length > 0 && kasPrice) {
    // Get last 7 days' earnings (assume sorted descending by date)
    const last7 = earnings.slice(0, 7);
    // Sum KAS earned per day
    const totalKas7 = last7.reduce((sum, e) => sum + (e.coins?.find(c => c.coin_ticker === 'KAS')?.coin_balance || 0), 0);
    avgKasPerDay7 = totalKas7 / last7.length;
    avgUsdPerDay7 = avgKasPerDay7 * kasPrice;

    // Calculate sofarThisMonth using earnings for the current month
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const earningsThisMonth = earnings.filter(e => {
      const d = new Date(e.earning_timestamp);
      return d >= startOfMonth && d <= now;
    });
    const kasSoFarThisMonth = earningsThisMonth.reduce((sum, e) => sum + (e.coins?.find(c => c.coin_ticker === 'KAS')?.coin_balance || 0), 0);
    sofarThisMonth = kasSoFarThisMonth * kasPrice;

    // Projected KAS for the rest of the month
    const msInDay = 24 * 60 * 60 * 1000;
    const daysElapsed = (now - startOfMonth) / msInDay;
    const daysRemaining = daysInMonth - Math.floor(daysElapsed);
    const projectedKasRestOfMonth = avgKasPerDay7 * daysRemaining;

    // totalKasThisMonth = actual so far + projected for rest of month
    totalKasThisMonth = kasSoFarThisMonth + projectedKasRestOfMonth;

    // Projected for the whole month (still use avgUsdPerDay7 for projection)
    projectedThisMonth = avgUsdPerDay7 * daysInMonth;
    projectedKASThisMonth = avgKasPerDay7 * daysInMonth;
  }
  // Determine if SHA256 is active
  const sha256Active = userData?.hashrate?.sha256?.hashrate > 0;

  const k1WorkerNames = Object.keys(k1PoolData?.miner?.workers || {});
  const hasK1Workers = Number(k1PoolData?.miner?.workersTotal) > 0;
  const k1ExpectedHashrate = k1WorkerNames.reduce((total, name) => {
    const powerPoolName = name === 'Neville'
      ? 'ks02'
      : name === 'Ronaldo'
        ? 'ks07'
        : name === 'Irwin'
          ? 'ks03'
          : name;
    return total + (getExpectedHashrate?.(powerPoolName) || 0);
  }, 0);
  const k1CurrentHashrate = (Number(k1PoolData?.miner?.curHashrate) || 0) / 1e12;
  let k1HashrateColor = '#fff';
  if (k1ExpectedHashrate > 0) {
    const k1HashratePercent = k1CurrentHashrate / k1ExpectedHashrate;
    if (k1HashratePercent >= 0.95) k1HashrateColor = '#22c55e';
    else if (k1HashratePercent >= 0.8) k1HashrateColor = '#f59e42';
    else k1HashrateColor = '#ef4444';
  }

  return (
    <div className="columns is-multiline">
      {false && (
      /* K1Pool Card - disabled while the dashboard uses PowerPool, RustyKaspa, and ZKAS. */
        <div className="column is-4" style={!isMobile ? { display: 'flex', flexDirection: 'column' } : {}}>
          <div className="box" style={!isMobile ? { ...styles.statCard, flex: 1 } : styles.statCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <p className="heading" style={styles.statLabel}>K1Pool KASPA</p>
                <p className="title" style={{ ...styles.statValue, color: k1HashrateColor }}>
                  {k1PoolData.miner.curHashrateStr || '0 H/s'}
                </p>
                <p style={styles.statSubLabel}>
                  Avg: {k1PoolData.miner.avgHashrateStr || '0 H/s'}
                  {k1ExpectedHashrate > 0 && (
                    <>, Expected: {formatNumber(k1ExpectedHashrate, 1)} TH/s</>
                  )}
                </p>
                <p style={styles.revenueLabel}>
                  Est. 24h: {formatNumber(k1PoolData.miner.coinsPerDay || 0, 2)} KAS
                </p>
                <p style={styles.balanceLabel}>
                  Workers: <span style={{ color: k1PoolData.miner.workersOnline === k1PoolData.miner.workersTotal ? '#22c55e' : '#ef4444' }}>
                    {k1PoolData.miner.workersOnline || 0}
                  </span> / {k1PoolData.miner.workersTotal || 0}
                </p>
                <p style={styles.balanceLabel}>
                  Balance: {formatNumber((k1PoolData.miner.pendingBalance || 0) + (k1PoolData.miner.immatureBalance || 0), 2)} KAS
                </p>
              </div>
              <a
                href="https://k1pool.com/pool/kaspa/account/KrJxvkR1cE683B99ArHLxx36snifu2zGctc"
                target="_blank"
                rel="noopener noreferrer"
                className="button is-info is-small"
              >
                View
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Hashrate Cards - kheavyhash always shown, sha256 only if active */}
      {hashrates
        .filter(hr => hr.algorithm === 'kheavyhash' || (hr.algorithm === 'sha256' && sha256Active))
        .map((hr, index) => {
          const poolAlgoData = hr.algorithm === 'kheavyhash' && poolData?.pools?.kheavyhash;
          const poolHashrate = poolAlgoData?.hashrate || 0;
          const poolHashrateUnits = poolAlgoData?.hashrate_units || 'TH/s';

          let myHashrateInPoolUnits = hr.hashrate;
          if (hr.units?.includes('TH') && poolHashrateUnits?.includes('PH')) {
            myHashrateInPoolUnits = hr.hashrate / 1000;
          } else if (hr.units?.includes('PH') && poolHashrateUnits?.includes('TH')) {
            myHashrateInPoolUnits = hr.hashrate * 1000;
          }

          const myPercentage = poolHashrate > 0 ? (myHashrateInPoolUnits / poolHashrate) * 100 : 0;

          let totalExpected = 0;
          let hashrateColor = styles.statValue?.color || undefined;

          if (hr.algorithm === 'kheavyhash') {
            if (Array.isArray(workerFinance)) {
              totalExpected = workerFinance
                .filter(f => f.Name && !f.DateEnded && typeof f.Hashrate === 'number' && f.Host === 'Bitcap')
                .reduce((sum, f) => sum + f.Hashrate, 0);
            }

            if (totalExpected === 0) {
              hashrateColor = '#fff';
            } else {
              const percent = hr.hashrate / totalExpected;
              if (percent >= 0.95) hashrateColor = '#22c55e'; // green
              else if (percent >= 0.8) hashrateColor = '#f59e42'; // orange
              else hashrateColor = '#ef4444'; // red
            }
          }

          return (
            <div className="column is-4" key={`${hr.algorithm}-${index}`} style={!isMobile ? { display: 'flex', flexDirection: 'column' } : {}}>
              <div className="box" style={!isMobile ? { ...styles.statCard, flex: 1 } : styles.statCard}>
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <p className="heading" style={styles.statLabel}>
                      Powerpool {hr.algorithm.toUpperCase()}
                    </p>
                    <p className="title" style={{ ...styles.statValue, color: hashrateColor }}>
                      {formatNumber(hr.hashrate, 1)} {hr.units}
                    </p>
                    <p style={styles.statSubLabel}>
                      Avg: {formatNumber(hr.avgHashrate, 1)} {hr.avgUnits}
                      {hr.algorithm === 'kheavyhash' && (
                        <> , Expected: {formatNumber(totalExpected, 1)} TH/s</>
                      )}
                    </p>
                    <p style={styles.revenueLabel}>
                      Est. 24h: ${formatNumber(hr.estimatedRevenue, 0)}
                    </p>
                    {poolAlgoData && (
                      <p style={styles.poolLabel}>
                        Pool: {formatNumber(poolHashrate, 1)} {poolHashrateUnits} ({formatNumber(myPercentage, 1)}%)
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

      {/* Workers Card */}
      <div className="column is-4" style={!isMobile ? { display: 'flex', flexDirection: 'column' } : {}}>
        <div className="box" style={!isMobile ? { ...styles.statCard, flex: 1 } : styles.statCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p className="heading" style={styles.statLabel}>Active Workers</p>
            <a
              href="https://powerpool.io/dashboard/7904bc362eb242b7b0d237d1bf84dc63"
              target="_blank"
              rel="noopener noreferrer"
              className="button is-info is-small"
            >
              View on PowerPool
            </a>
          </div>
          <p className="title" style={styles.statValue}>
            <span style={{ color: totalColor }}>{activeWorkers}</span>
            <span style={styles.statSubtext}> / {(() => {
              if (!Array.isArray(workers)) return 0;
              return workers.filter(w => w.algorithm === 'kheavyhash' || (w.algorithm === 'sha256' && w.hashrate > 0)).length;
            })()}</span>
          </p>
          {(() => {
            const kasBalance = balances.find(b => b.coinTicker === 'KAS');
            const usdcBalance = balances.find(b => b.coinTicker === 'USDC');
            return (
              <>
                {kasBalance && (
                  <p style={styles.balanceLabel}>
                    Balance: {formatNumber(kasBalance.balance, 0)} KAS
                  </p>
                )}
                {usdcBalance && (
                  <p style={styles.balanceLabel}>
                    Balance: {formatNumber(usdcBalance.balance, 0)} USDC
                  </p>
                )}
              </>
            );
          })()}
        </div>
      </div>

   
      {/* Recent Activity Card */}
      <div className="column is-4" style={!isMobile ? { display: 'flex', flexDirection: 'column' } : {}}>
        <div className="box" style={!isMobile ? { ...styles.statCard, flex: 1, position: 'relative' } : { ...styles.statCard, position: 'relative' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <p className="heading" style={styles.statLabel}>Latest</p>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.0rem', ...styles.timerText, fontWeight: 500 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', color: '#60a5fa' }}>
                <path d="M12 4v-3l-4 4 4 4v-3c3.31 0 6 2.69 6 6 0 1.010-0.25 1.97-0.7 2.8l1.46 1.46c0.78-1.23 1.24-2.69 1.24-4.26 0-4.42-3.58-8-8-8zM12 18c-3.31 0-6-2.69-6-6 0-1.010 0.25-1.97 0.7-2.8l-1.46-1.46c-0.78 1.23-1.24 2.69-1.24 4.26 0 4.42 3.58 8 8 8v3l4-4-4-4v3z" fill="currentColor" />
              </svg>
              <span style={{ minWidth: 44, textAlign: 'right' }}>
                {typeof timeUntilRefresh === 'number' && formatTime ? formatTime(timeUntilRefresh) : (timeUntilRefresh || '')}
              </span>
            </span>
          </div>

          <p style={styles.balanceLabel}>
            {userData?.payments?.[0] ? (() => {
              const today = new Date();
              const paymentDate = new Date(userData.payments[0].date);
              const isToday = paymentDate.toDateString() === today.toDateString();
              return isToday
                ? `Payment today (${paymentDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}): ${formatNumber(userData.payments[0].value, 0)}`
                : `Payment: ${formatNumber(userData.payments[0].value, 0)} on ${paymentDate.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}`;
            })() : 'Payment: N/A'}
          </p>

          <p style={styles.balanceLabel}>
            {earnings?.[0] ? (() => {
              const today = new Date();
              const yesterday = new Date(today);
              yesterday.setDate(today.getDate() - 1);
              const earningDate = new Date(earnings[0].earning_timestamp);
              const isYesterday = earningDate.toDateString() === yesterday.toDateString();
              // Find KAS and USDC coins for this earning
              const kasCoin = earnings[0].coins?.find(c => c.coin_ticker === 'KAS');
              const usdcCoin = earnings[0].coins?.find(c => c.coin_ticker === 'USDC');
              const kasStr = kasCoin ? `${formatNumber(kasCoin.coin_balance, 0)} KAS` : null;
              const usdcStr = usdcCoin ? `${formatNumber(usdcCoin.coin_balance, 0)} USDC` : null;
              const bothStr = [kasStr, usdcStr].filter(Boolean).join(', ');
              return isYesterday
                ? `Earnings yesterday: ${bothStr || formatNumber(earnings[0].earnings, 0)}`
                : `Earnings: ${bothStr || formatNumber(earnings[0].earnings, 0)} on ${earningDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}`;
            })() : 'Earnings: N/A'}
          </p>


          <p style={styles.balanceLabel}>
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              Price: {priceLoading ? 'Loading...' : priceError ? 'Error' : `$${formatNumber(kasPrice, 4)}`}
              {kasChange24h !== undefined && kasChange24h !== null && !priceLoading && !priceError && (
                <span style={{ marginLeft: 6, fontWeight: 500, display: 'inline-flex', alignItems: 'center', minWidth: 64 }}>
                  {kasChange24h > 0 && (
                    <span style={{ color: '#22c55e', display: 'inline-flex', alignItems: 'center' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: 2 }}><path d="M12 5l6 7H6l6-7z" fill="currentColor"/></svg>
                      {Math.abs(kasChange24h).toFixed(2)}%
                    </span>
                  )}
                  {kasChange24h < 0 && (
                    <span style={{ color: '#ef4444', display: 'inline-flex', alignItems: 'center' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: 2, transform: 'rotate(180deg)' }}><path d="M12 5l6 7H6l6-7z" fill="currentColor"/></svg>
                      {Math.abs(kasChange24h).toFixed(2)}%
                    </span>
                  )}
                  {kasChange24h === 0 && (
                    <span style={{ color: '#e2e8f0', display: 'inline-flex', alignItems: 'center' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: 2, opacity: 0.7 }}><circle cx="12" cy="12" r="5" fill="currentColor"/></svg>
                      0.00%
                    </span>
                  )}
                </span>
              )}
            </span>
          </p>

          <div style={{ ...styles.balanceLabel, display: 'grid', gridTemplateColumns: '72px minmax(0, 1fr)', columnGap: 8, rowGap: 4 }}>
            <span>Mining:</span>
            <span>
              {miningTotal !== null && miningTotal !== undefined
                ? `${formatNumber(miningTotal, 0)} KAS ($${formatNumber(miningTotal * (kasPrice || 0), 0)})`
                : miningBalanceLoading ? 'Loading...'
                  : miningBalanceError || 'Unavailable'}
              {miningBalanceLoading && miningTotal !== null && miningTotal !== undefined && (
                <span style={{ color: '#94a3b8', fontSize: 11 }}> Updating…</span>
              )}
            </span>
            {usdcMiningBalance !== null && usdcMiningBalance !== undefined && <>
              <span>USDC:</span>
              <span>${formatNumber(usdcMiningBalance, 0)} USDC</span>
            </>}
            {miningTotal !== null && miningTotal !== undefined && miningBalanceError && (
              <span style={{ gridColumn: '1 / -1', color: '#fbbf24', fontSize: 11 }}>{miningBalanceError}</span>
            )}
          </div>

          {isFinanceTab && (
            <div style={styles.balanceLabel}>
              Ledger: {ledgerTotal ? formatNumber(ledgerTotal, 0) : '0'}
            </div>
          )}
          {isFinanceTab && (
            <div style={styles.balanceLabel}>
              <span>
                Electric (next month): ${formatNumber(electricNextMonth, 0)}
                <span style={{ opacity: 0.7 }}>
                  {' '}(${formatNumber(totalElectricPerDay, 2)}/day)
                </span>
              </span>
              {/* Progress bar for mining vs electric cost */}
              {electricNextMonth > 0 && (
                <div style={{ marginTop: 6, width: '50%' }}>
                  <div style={{
                    background: '#334155',
                    borderRadius: 4,
                    height: 10,
                    width: '100%',
                    overflow: 'hidden',
                    boxShadow: 'inset 0 1px 2px #0004'
                  }}>
                    <div style={{
                      width: `${Math.min(100, ((miningTotal * (kasPrice || 0)) / electricNextMonth) * 100).toFixed(1)}%`,
                      background: '#22c55e',
                      height: '100%',
                      transition: 'width 0.5s',
                    }} />
                  </div>
                  <div style={{ fontSize: 12, color: '#e2e8f0', marginTop: 2, textAlign: 'right', opacity: 0.7 }}>
                    {formatNumber(miningTotal * (kasPrice || 0), 0)} / {formatNumber(electricNextMonth, 0)} USD
                    {' '}({((miningTotal * (kasPrice || 0)) / electricNextMonth * 100).toFixed(1)}%)
                  </div>
                </div>
              )}
              {/* Progress bar for how far through the current month we are, turns red if behind electric earnings */}
              {(() => {
                const now = new Date();
                const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
                const percentMonth = Math.min(100, ((now - startOfMonth) / (endOfMonth - startOfMonth)) * 100);
                const percentElectric = electricNextMonth > 0 ? ((miningTotal * (kasPrice || 0)) / electricNextMonth * 100) : 0;
                const barColor = percentMonth > percentElectric ? '#ef4444' : '#60a5fa';
                return (
                  <div style={{ marginTop: 6, width: '50%' }}>
                    <div style={{
                      background: '#334155',
                      borderRadius: 4,
                      height: 8,
                      width: '100%',
                      overflow: 'hidden',
                      boxShadow: 'inset 0 1px 2px #0004'
                    }}>
                      <div style={{
                        width: `${percentMonth.toFixed(1)}%`,
                        background: barColor,
                        height: '100%',
                        transition: 'width 0.5s',
                      }} />
                    </div>
                    <div style={{ fontSize: 11, color: '#e2e8f0', marginTop: 2, textAlign: 'right', opacity: 0.7 }}>
                      {percentMonth.toFixed(0)}% of month
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          {isFinanceTab && (
            <div style={styles.balanceLabel}>
              <span>
                Projected This Month: ${formatNumber(projectedThisMonth, 0)} ({formatNumber(totalKasThisMonth,0)} KAS)
              </span>
              {/* Progress bar for projected earnings this month vs electric cost for next month */}
              {electricNextMonth > 0 && (
                <div style={{ marginTop: 6, width: '100%' }}>
                  <div style={{
                    background: '#334155',
                    borderRadius: 4,
                    height: 10,
                    width: '100%',
                    overflow: 'hidden',
                    boxShadow: 'inset 0 1px 2px #0004'
                  }}>
                    <div style={{
                      width: `${Math.min(100, ((projectedThisMonth) / electricNextMonth) * 100).toFixed(1)}%`,
                      background: '#22c55e',
                      height: '100%',
                      transition: 'width 0.5s',
                    }} />
                  </div>
                  <div style={{ fontSize: 12, color: '#e2e8f0', marginTop: 2, textAlign: 'right', opacity: 0.7 }}>
                    {formatNumber(projectedThisMonth,0)} / {formatNumber(electricNextMonth, 0)} USD
                    {' '}({((projectedThisMonth) / electricNextMonth * 100).toFixed(1)}% of next month's electric)
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardCards;
